import {
  DUNA_CATERING_PREFERENCE_OPTIONS,
  normalizeCateringPreferenceSelection,
} from './duna-catering-preference-options';

const preferenceLabelMap = new Map(
  DUNA_CATERING_PREFERENCE_OPTIONS.map((opt) => [opt.value, opt.label])
);

export function preferenceSummaryLabel(key: string): string {
  const canonical =
    key === 'ingen_praeferencer' ? 'nothing' : key === 'halal' ? 'hallal' : key;
  return preferenceLabelMap.get(canonical) || canonical;
}

/** Display label for combo summary keys (e.g. glutenfri|vegetar → Glutenfri & Vegetar). */
export function preferenceComboSummaryLabel(comboKey: string): string {
  if (!comboKey || comboKey === 'nothing' || comboKey === 'ingen_praeferencer') {
    return preferenceSummaryLabel('nothing');
  }
  return comboKey
    .split('|')
    .map((part) => preferenceSummaryLabel(part.trim()))
    .join(' & ');
}

export function preferenceComboSummaryKey(prefs: string[] | null | undefined): string {
  const normalized = normalizeCateringPreferenceSelection(prefs || []);
  if (normalized.length === 0) {
    return 'nothing';
  }
  return normalized.join('|');
}

/** Canonicalize a summary map key (legacy single values or alias combos). */
export function canonicalPreferenceComboSummaryKey(comboKey: string): string {
  const trimmed = (comboKey ?? '').trim();
  if (!trimmed) {
    return 'nothing';
  }
  if (!trimmed.includes('|')) {
    return preferenceComboSummaryKey([trimmed]);
  }
  return preferenceComboSummaryKey(trimmed.split('|'));
}

/** Merge counts for keys that represent the same preference combination. */
export function mergeCateringPreferenceSummary(
  summary: Record<string, number> | null | undefined
): Record<string, number> {
  const merged: Record<string, number> = {};
  if (!summary) {
    return merged;
  }
  for (const [rawKey, rawCount] of Object.entries(summary)) {
    const count = rawCount ?? 0;
    if (count <= 0) {
      continue;
    }
    const key = canonicalPreferenceComboSummaryKey(rawKey);
    merged[key] = (merged[key] ?? 0) + count;
  }
  return merged;
}

export type CateringApprovalStatus = 'pending' | 'needs_reapproval' | 'approved';

export function cateringApprovalStatusLabel(
  status: CateringApprovalStatus | string | null | undefined
): string {
  if (status === 'approved') {
    return 'Godkendt';
  }
  if (status === 'needs_reapproval') {
    return 'Kræver gen-godkendelse';
  }
  return 'Afventer';
}

export function cateringApprovalStatusColor(
  status: CateringApprovalStatus | string | null | undefined
): 'success' | 'warn' | 'default' {
  if (status === 'approved') {
    return 'success';
  }
  if (status === 'needs_reapproval') {
    return 'warn';
  }
  return 'default';
}
